/**
 * The quest being followed, kept in the corner of the eye.
 *
 * Not the journal: the journal is for reading about every quest there is, and
 * this is for the one being done right now. It says which quest, how far along,
 * what the step wants carried, and what to do. Nothing else, because anything
 * else would have to be read rather than glanced at.
 *
 * Shown only while a quest is being followed, and closing it stops following.
 */

import type { ItemStack } from '@inverse/common';
import type { ItemDefinitionWire, QuestWire } from '@inverse/protocol';
import { useState } from 'react';
import { useDraggableWindow } from './draggable';
import { placeSaid, wantsOf } from './wants';

export function QuestTracker({
  quest,
  step,
  inventory,
  worn,
  toolbelt,
  items,
  marks,
  onMarks,
  onStop,
}: {
  quest: QuestWire;
  /** How far along, counting from one for the step being worked on. */
  step: number;
  inventory: readonly (ItemStack | null)[];
  /** What is on, and what is on the belt: a step can ask for either. */
  worn: readonly (ItemStack | null)[];
  toolbelt: readonly (ItemStack | null)[];
  items: readonly ItemDefinitionWire[];
  /** Whether the arrows in the world are being drawn. */
  marks: boolean;
  onMarks: (marks: boolean) => void;
  onStop: () => void;
}): JSX.Element | null {
  const [folded, setFolded] = useState(false);
  const { attach, style, onPointerDown } = useDraggableWindow('tracker');

  const doing = quest.steps[step - 1];
  if (!doing) return null;

  // How far along, as a fraction. The step being worked on counts as reached
  // rather than done, which is what somebody means by "three of nine".
  const many = Math.max(1, quest.steps.length);
  const along = Math.min(1, Math.max(0, (step - 1) / many));

  // Everything the step waits to be holding, and how far along each one is.
  // Only shown when the step actually asks for something: a line about nothing
  // is a line in the way.
  const wants = wantsOf(doing, { inventory, worn, toolbelt });
  const named = (itemId: number): string => items.find((item) => item.id === itemId)?.name ?? 'it';

  return (
    <div className={folded ? 'tracker folded' : 'tracker'} ref={attach} style={style}>
      <div className="tracker-head drag-handle" onPointerDown={onPointerDown}>
        <span className="tracker-name">{quest.name}</span>

        {/* The arrows in the world, turned on and off from here: it is the same
            question as whether to be shown where to go. An arrow rather than a
            word, since the button is what it turns on. */}
        <button
          type="button"
          className={marks ? 'tracker-button on' : 'tracker-button'}
          title={marks ? 'Stop showing the way' : 'Show the way'}
          aria-label={marks ? 'Stop showing the way' : 'Show the way'}
          onClick={() => onMarks(!marks)}
        >
          <span className="tracker-mark" aria-hidden />
        </button>

        <button
          type="button"
          className="tracker-button"
          title={folded ? 'Open it again' : 'Fold it away'}
          aria-label={folded ? 'Open it again' : 'Fold it away'}
          onClick={() => setFolded(!folded)}
        >
          {folded ? '▢' : '▁'}
        </button>

        <button
          type="button"
          className="tracker-button"
          title="Stop following this quest"
          aria-label="Stop following this quest"
          onClick={onStop}
        >
          ✕
        </button>
      </div>

      {folded ? null : (
        <div className="tracker-body">
          {/* The bar is the line between the name and the work, and says how
              far along it is by how far it has filled. */}
          <div className="tracker-bar" title={`Step ${step} of ${quest.steps.length}`}>
            <span className="tracker-bar-filled" style={{ width: `${Math.round(along * 100)}%` }} />
          </div>

          {wants.length > 0 ? (
            <ul className="tracker-wants">
              {wants.map((want, which) => {
                // A count is worth saying for a pile of logs and not worth
                // saying for one pickaxe, which is either on or not.
                const many = Math.max(1, want.count);
                const where = placeSaid(want.where);

                return (
                  <li
                    key={`${want.itemId}-${want.where}-${which}`}
                    className={want.met ? 'tracker-needs met' : 'tracker-needs'}
                  >
                    {named(want.itemId)}
                    {where ? ` ${where}` : ''} {many > 1 ? `${want.have} of ${many}` : ''}
                  </li>
                );
              })}
            </ul>
          ) : null}

          <p className="tracker-doing">{doing.text}</p>
          {doing.note ? <p className="tracker-note">{doing.note}</p> : null}
        </div>
      )}
    </div>
  );
}
